import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "layout__wrapper" }
const _hoisted_2 = { class: "layout__wrapper" }

import { computed, onBeforeMount, Raw, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  RouteLocationNormalized,
  useRoute,
  useRouter,
} from 'vue-router'

import { backend } from '@/services/backend'

import { Sentry } from '@/services/sentry'
import { assertNotNull } from '@/helpers/typing'

import { ArticleView } from '@/models/doc.article'
import { backLink } from '@/models/backlink'
import { streaks16464Experiment } from '@/services/ab'

import Layout from '@/app/components/Layout.vue'
import ArticleContent from '@/app/components/article/Content.vue'
import LoadingError from '@/components/ui/LoadingError.vue'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'ArticlePage',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const wait = useWait()

const article = ref<Raw<ArticleView> | null>(null)
const isInitialLoad = ref(true)

onBeforeRouteUpdate(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    // Called when the route that renders this component has changed,
    // but this component is reused in the new route.
    const articleValue = assertNotNull(article.value)
    await articleValue.initPage(to, from, loadDocument)
    backLink.value.updateMetaTitle(from, articleValue.title)

    next()
  },
)

onBeforeRouteLeave(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    const articleValue = assertNotNull(article.value)
    await articleValue.updateReadTimeAndSavePosition()

    next()
  },
)

onBeforeMount(async () => {
  wait.start('Loading Doc')
  // Start the reading streak experiment
  await streaks16464Experiment()

  await loadDocument(route)
})

async function _fetchDocument(
  route: RouteLocationNormalized,
): Promise<ArticleView> {
  const response = await backend.getArticle(route.params.url_slug as string)
  return new ArticleView(response)
}

async function loadDocument(route: RouteLocationNormalized): Promise<void> {
  try {
    wait.start('Loading Doc')
    wait.end('Error Loading Doc')
    article.value = await _fetchDocument(route)
    await article.value.initDocument(isInitialLoad.value)
    // Don't restore the reading position in the next pages.
    isInitialLoad.value = false
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else {
      wait.start('Error Loading Doc')
      Sentry.captureException(error)
    }
  } finally {
    wait.end('Loading Doc')
  }
}

const isLoadingDoc = computed(() => {
  return wait.is('Loading Doc')
})

const isErrorLoadingDoc = computed(() => {
  return wait.is('Error Loading Doc')
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (isLoadingDoc.value)
      ? (_openBlock(), _createBlock(Layout, { key: 0 }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(LoadingSpinner)
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (article.value)
      ? (_openBlock(), _createBlock(ArticleContent, {
          key: 1,
          article: article.value
        }, null, 8, ["article"]))
      : _createCommentVNode("", true),
    (!article.value && isErrorLoadingDoc.value)
      ? (_openBlock(), _createBlock(Layout, { key: 2 }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(LoadingError)
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})